/**
 * ModelSelector - Reusable AI model selection component
 *
 * 4 model tiers:
 * - Standard (Gemini 3 Flash) - Default, best balance
 * - Budget (Gemini 2.0 Flash-Lite) - Lowest cost
 * - Power (Gemini 3 Pro) - Maximum quality
 * - Custom (from AI Settings) - User-configured
 */

import { Zap, Sparkles, Coins, Settings, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// Model tier definitions
export type ModelTier = "standard" | "budget" | "power" | "custom";

export interface ModelOption {
  id: string;
  name: string;
  fullName: string;
  description: string;
  tier: ModelTier;
  costPerMInputTokens?: number;
  costPerMOutputTokens?: number;
  settingsUrl?: string;
}

// Available AI models for extraction — pricing per 1M tokens (USD)
export const AVAILABLE_MODELS: ModelOption[] = [
  {
    id: "gemini-3-flash-preview",
    name: "Standard",
    fullName: "Gemini 3.0 Flash",
    description: "Best balance of quality and cost (recommended)",
    tier: "standard",
    costPerMInputTokens: 0.075,
    costPerMOutputTokens: 0.3,
  },
  {
    id: "gemini-2.5-flash-lite",
    name: "Budget",
    fullName: "Gemini 2.5 Flash-Lite",
    description: "Lowest cost, good for simple documents",
    tier: "budget",
    costPerMInputTokens: 0.02,
    costPerMOutputTokens: 0.08,
  },
  {
    id: "gemini-3-pro-preview",
    name: "Power",
    fullName: "Gemini 3.0 Pro",
    description: "Maximum quality for complex documents",
    tier: "power",
    costPerMInputTokens: 1.25,
    costPerMOutputTokens: 5.0,
  },
];

// Custom model option (user-configured from AI settings)
export const CUSTOM_MODEL_OPTION: ModelOption = {
  id: "custom",
  name: "Custom",
  fullName: "User-Configured Model",
  description: "Use model from AI Settings",
  tier: "custom",
  settingsUrl: "/ai-settings",
};

export type ModelId = string;

// Helper to get model by ID
export function getModelById(id: ModelId): ModelOption {
  if (id === "custom") return CUSTOM_MODEL_OPTION;
  return AVAILABLE_MODELS.find(m => m.id === id) || AVAILABLE_MODELS[0];
}

// Get tier icon component
export function getTierIcon(tier: ModelTier) {
  switch (tier) {
    case "standard":
      return Zap;
    case "budget":
      return Coins;
    case "power":
      return Sparkles;
    case "custom":
      return Settings;
    default:
      return Zap;
  }
}

// Get tier color class
export function getTierColor(tier: ModelTier) {
  switch (tier) {
    case "standard":
      return "text-primary";
    case "budget":
      return "text-success";
    case "power":
      return "text-warning";
    case "custom":
      return "text-muted-foreground";
    default:
      return "text-primary";
  }
}

// Get tier badge variant
export function getTierBadgeVariant(tier: ModelTier) {
  switch (tier) {
    case "standard":
      return "default";
    case "budget":
      return "secondary";
    case "power":
      return "outline";
    case "custom":
      return "outline";
    default:
      return "default";
  }
}

// ============================================================================
// COMPONENT VARIANTS
// ============================================================================

interface ModelSelectorProps {
  value: ModelId;
  onChange: (modelId: ModelId) => void;
  disabled?: boolean;
  className?: string;
  /** Show full select dropdown vs compact button */
  variant?: "select" | "compact" | "inline";
  /** Show description below selector */
  showDescription?: boolean;
  /** Show cost information */
  showCost?: boolean;
}

export function ModelSelector({
  value,
  onChange,
  disabled = false,
  className,
  variant = "select",
  showDescription = true,
  showCost = false,
}: ModelSelectorProps) {
  const currentModel = getModelById(value);
  const TierIcon = getTierIcon(currentModel.tier);

  if (variant === "inline") {
    return (
      <TooltipProvider>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  className={cn("gap-1.5 h-7 px-2", className)}
                >
                  <TierIcon
                    className={cn(
                      "h-3.5 w-3.5",
                      getTierColor(currentModel.tier)
                    )}
                  />
                  <span className="text-xs">{currentModel.name}</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">{currentModel.fullName}</p>
              <p className="text-xs text-muted-foreground">
                {currentModel.description}
              </p>
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" className="w-56">
            {AVAILABLE_MODELS.map(model => {
              const ModelIcon = getTierIcon(model.tier);
              return (
                <DropdownMenuItem
                  key={model.id}
                  onClick={() => onChange(model.id)}
                  className={cn(value === model.id && "bg-accent")}
                >
                  <ModelIcon
                    className={cn("h-4 w-4 mr-2", getTierColor(model.tier))}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{model.name}</span>
                      {model.tier === "standard" && (
                        <Badge variant="secondary" className="text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {model.fullName}
                    </span>
                  </div>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onChange("custom")}>
              <Settings className="h-4 w-4 mr-2 text-muted-foreground" />
              <div className="flex-1">
                <span className="font-medium">Custom</span>
                <span className="text-xs text-muted-foreground block">
                  from AI Settings
                </span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipProvider>
    );
  }

  if (variant === "compact") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className={cn("gap-2", className)}
          >
            <TierIcon
              className={cn("h-4 w-4", getTierColor(currentModel.tier))}
            />
            <span>{currentModel.name}</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {AVAILABLE_MODELS.map(model => {
            const ModelIcon = getTierIcon(model.tier);
            return (
              <DropdownMenuItem
                key={model.id}
                onClick={() => onChange(model.id)}
                className={cn("py-2", value === model.id && "bg-accent")}
              >
                <ModelIcon
                  className={cn("h-4 w-4 mr-2", getTierColor(model.tier))}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{model.name}</span>
                    {model.tier === "standard" && (
                      <Badge variant="secondary" className="text-xs">
                        Default
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {model.description}
                  </span>
                  {showCost && model.costPerMInputTokens && (
                    <span className="text-xs text-muted-foreground block">
                      ${model.costPerMInputTokens}/${model.costPerMOutputTokens}{" "}
                      per M tokens
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onChange("custom")} className="py-2">
            <Settings className="h-4 w-4 mr-2 text-muted-foreground" />
            <div className="flex-1">
              <span className="font-medium">Custom</span>
              <span className="text-xs text-muted-foreground block">
                Configure in AI Settings
              </span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default: full select dropdown
  return (
    <div className={cn("space-y-2", className)}>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-9">
          <SelectValue>
            <div className="flex items-center gap-2">
              <TierIcon
                className={cn("h-3.5 w-3.5", getTierColor(currentModel.tier))}
              />
              <span>{currentModel.name}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {AVAILABLE_MODELS.map(model => {
            const ModelIcon = getTierIcon(model.tier);
            return (
              <SelectItem key={model.id} value={model.id}>
                <div className="flex items-center gap-2">
                  <ModelIcon
                    className={cn("h-3.5 w-3.5", getTierColor(model.tier))}
                  />
                  <span className="font-medium">{model.name}</span>
                  <span className="text-muted-foreground text-xs">
                    ({model.fullName})
                  </span>
                  {model.tier === "standard" && (
                    <Badge variant="secondary" className="text-xs ml-1">
                      Default
                    </Badge>
                  )}
                </div>
              </SelectItem>
            );
          })}
          {/* Custom model option */}
          <SelectItem value="custom">
            <div className="flex items-center gap-2">
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium">Custom</span>
              <span className="text-muted-foreground text-xs">
                (from AI Settings)
              </span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
      {showDescription && (
        <p className="text-xs text-muted-foreground">
          {currentModel.description}
          {currentModel.tier === "custom" && (
            <a
              href="/ai-settings"
              className="ml-1 text-primary hover:underline"
            >
              Configure →
            </a>
          )}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// DISPLAY ONLY BADGE
// ============================================================================

interface ModelBadgeProps {
  modelId: ModelId;
  className?: string;
  showFullName?: boolean;
}

export function ModelBadge({
  modelId,
  className,
  showFullName = false,
}: ModelBadgeProps) {
  const model = getModelById(modelId);
  const TierIcon = getTierIcon(model.tier);

  return (
    <Badge
      variant={getTierBadgeVariant(model.tier)}
      className={cn("gap-1", className)}
    >
      <TierIcon className={cn("h-3 w-3", getTierColor(model.tier))} />
      {showFullName ? model.fullName : model.name}
    </Badge>
  );
}

export default ModelSelector;
